import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { AdminInviteForm } from './FacilityForm/AdminInviteForm';
import { StatusMessage } from '../../../Hubs/HubDetails/AssignHubController/components/StatusMessage';
import { AssignFacilitySelect } from './FacilityAdmin/AssignFacility';
import { ReactComponent as UpdateSwSuccessIcon } from '../../../../images/icons/update_sw_success.svg';
import { ReactComponent as FailedToAddUserIcon } from '../../../../images/icons/adding_user_failed.svg';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import {
  addParentFacilityAdmin,
  inviteFacilityAdmin,
  setAddParentFacilityAdminStatus,
} from '../../../../store/facility/actionCreators';
import { FACILITY_ROLE } from '../../../../shared/constants';

const STEPS = {
  ADMIN_INVITE: 'admin_invite',
  FACILITY_SELECT: 'facility_select',
  SUCCESS: 'success',
  ERROR: 'error',
};

const AddFacilityAdminController = ({
  onClose,
  onBack,
  isFacilityAdmin,
}: {
  onClose: () => void;
  onBack: () => void;
  isFacilityAdmin?: boolean;
}) => {
  const dispatch = useDispatch();

  const { addParentFacilityAdminStatus, isLoading } = useSelector(
    (state: RootState) => state.facility,
  );

  // A facility admin can only add a location admin, and that invite is sent
  // straight from the details form — there is no facility-select step after it.
  const mode = isFacilityAdmin ? 'location' : 'facility';

  const hasSubmitted = useRef(false);
  const [currentStep, setCurrentStep] = useState(STEPS.ADMIN_INVITE);
  const [adminData, setAdminData] = useState<{
    email: string;
    first_name: string;
    last_name: string;
  } | null>(null);

  const handleAdminInvite = async (values: {
    email: string;
    first_name: string;
    last_name: string;
  }) => {
    setAdminData(values);
    setCurrentStep(STEPS.FACILITY_SELECT);
  };

  // handle submit for location admin invite
  const handleLocationAdminInvite = async (values: {
    email: string;
    first_name: string;
    last_name: string;
  }) => {
    // API expects facility_role_id = 1 for location admin
    const data = {
      ...values,
      facility_role_id: FACILITY_ROLE.FACILITY_ADMIN,
    };
    hasSubmitted.current = true;
    dispatch(inviteFacilityAdmin(data));
  };

  const handleFacilitySelect = (facilityId: number) => {
    if (!adminData) return;

    const data = {
      first_name: adminData.first_name,
      last_name: adminData.last_name,
      email: adminData.email,
      parent_facility_role_id: String(FACILITY_ROLE.FACILITY_ADMIN), // currently only one role
    };

    hasSubmitted.current = true;
    dispatch(addParentFacilityAdmin(facilityId, data));
  };

  // The status is global and outlives the flow that set it, so only react to it
  // once this modal has actually submitted something.
  useEffect(() => {
    if (!hasSubmitted.current) return;
    if (addParentFacilityAdminStatus !== null) {
      setCurrentStep(
        addParentFacilityAdminStatus ? STEPS.SUCCESS : STEPS.ERROR,
      );
      dispatch(setAddParentFacilityAdminStatus(null));
    }
  }, [addParentFacilityAdminStatus, dispatch]);

  const renderStep = () => {
    switch (currentStep) {
      case STEPS.ADMIN_INVITE:
        return (
          <AdminInviteForm
            actionType="add"
            mode={mode}
            submitLabel={isFacilityAdmin ? 'Add' : 'Next'}
            onSubmit={
              isFacilityAdmin ? handleLocationAdminInvite : handleAdminInvite
            }
            onBack={onBack}
            isFacilityAdmin={isFacilityAdmin}
          />
        );
      case STEPS.FACILITY_SELECT:
        return (
          <AssignFacilitySelect
            onSelect={handleFacilitySelect}
            onBack={() => setCurrentStep(STEPS.ADMIN_INVITE)}
          />
        );
      case STEPS.SUCCESS:
        return (
          <StatusMessage
            icon={<UpdateSwSuccessIcon />}
            title="Added successfully!"
            onClose={onClose}
          />
        );
      case STEPS.ERROR:
        return (
          <StatusMessage
            icon={<FailedToAddUserIcon />}
            title={
              mode === 'location'
                ? 'Location admin didn’t save'
                : 'Facility admin didn’t save'
            }
            message={'Please try again or come back later.'}
            onClose={() => {
              setCurrentStep(STEPS.ADMIN_INVITE);
            }}
            primaryButtonText="Try again"
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress />
      </Box>
    );
  }
  return renderStep();
};

export default AddFacilityAdminController;
